import { useCallback, useEffect, useId, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/shared/auth';
import { useOrg, type OrgRole } from '@/shared/org';
import { Icon } from '../components/ui/Icon';
import { useI18n } from '../i18n';
import { AccessLevelSelect } from './AccessLevelSelect';
import { shareLink } from './shareLink';
import {
  ShareError,
  type AccessLevel,
  type AccessRequest,
  type ShareAccess,
  type ShareAdapter,
  type ShareErrorCode,
  type ShareGrant,
  type PublicAccess,
  type SharePrincipal,
  type ShareSubject,
} from './types';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const LADDER: readonly OrgRole[] = ['admin', 'coordinator', 'member', 'viewer', 'developer'];

/** Two initials, or the first letter of the email when there is no name yet. */
function initials(name: string | null, email: string): string {
  const source = name?.trim() || email;
  const parts = source.split(/[\s@._-]+/).filter(Boolean).slice(0, 2);
  return parts.map((part) => part[0]).join('').toUpperCase();
}

function errorCodeOf(error: unknown): ShareErrorCode {
  return error instanceof ShareError ? error.code : 'unknown';
}

interface Props {
  subject: ShareSubject;
  adapter: ShareAdapter;
  onClose: () => void;
}

/**
 * The "Share" dialog, ported from TMS `DialogoCompartir`.
 *
 * What carried over: the layout (add row on top, then who has access), the
 * inline level picker per person, the optimistic update with rollback, and the
 * separate read-only section for people whose access comes from their role.
 *
 * What changed, and why:
 *  - The TMS version listed EVERY user in a `<select>`. Here the candidate list
 *    comes from the adapter and is scoped to one organization; it feeds a
 *    datalist so you can also type an email that is not a member yet, like
 *    Folders does.
 *  - Adding someone who has no account creates an invitation, which is a Worker
 *    call. The component does not know or decide that — the adapter does.
 *  - Escape, focus trap and `aria-modal` are new. The TMS dialog had none.
 */
export function ShareDialog({ subject, adapter, onClose }: Props) {
  const { t } = useI18n();
  const org = useOrg();
  const session = useSession();
  const userRole: OrgRole = org.status === 'ready' ? org.role : 'viewer';
  const isPrivileged = userRole === 'owner' || userRole === 'developer' || userRole === 'admin';

  const titleId = useId();
  const emailFieldId = useId();
  const listId = useId();

  const panelRef = useRef<HTMLDivElement>(null);
  const [access, setAccess] = useState<ShareAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ShareErrorCode | null>(null);
  const [notice, setNotice] = useState<'granted' | 'invited' | 'invitedEmail' | null>(null);
  const [noticeInviteLink, setNoticeInviteLink] = useState<string | null>(null);
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
  const [email, setEmail] = useState('');
  const [level, setLevel] = useState<AccessLevel>('view');
  const [submitting, setSubmitting] = useState(false);
  const [publicAccess, setPublicAccess] = useState<PublicAccess | null>(null);
  const [publicSaving, setPublicSaving] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const [externalPromptOpen, setExternalPromptOpen] = useState(false);
  const [notInOrgAlertOpen, setNotInOrgAlertOpen] = useState(false);
  const [alertReason, setAlertReason] = useState<'not_privileged' | 'admin_disabled' | 'item_disabled'>('not_privileged');
  const [addToOrgOpen, setAddToOrgOpen] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');

  const [itemAllowExternal, setItemAllowExternal] = useState(true);
  const [itemAllowExternalSaving, setItemAllowExternalSaving] = useState(false);
  const [adminAllowExternal, setAdminAllowExternal] = useState(true);

  const [addName, setAddName] = useState('');
  const [addRole, setAddRole] = useState<OrgRole>('member');
  const [addTeam, setAddTeam] = useState('');
  const [teamsList, setTeamsList] = useState<Array<{ id: string; name: string }>>([]);
  const [addingToOrgBusy, setAddingToOrgBusy] = useState(false);
  const [addToOrgError, setAddToOrgError] = useState<string | null>(null);

  const load = useCallback(
    async (signal: AbortSignal) => {
      setLoading(true);
      try {
        const next = await adapter.load(subject, signal);
        if (!signal.aborted) {
          setAccess(next);
          setPublicAccess(next.publicAccess ?? null);
          setError(null);

          // Fetch item external sharing setting
          if (subject.kind === 'resource') {
            const { data: nodeData } = await supabase
              .from('folders_nodes')
              .select('allow_external_sharing')
              .eq('id', subject.resourceId)
              .maybeSingle();
            setItemAllowExternal(nodeData?.allow_external_sharing ?? true);
          } else if (subject.kind === 'module' && org.status === 'ready') {
            const { data: moduleSetting } = await supabase
              .from('org_module_external_settings')
              .select('allow_external_sharing')
              .eq('org_id', org.orgId)
              .eq('module_key', subject.moduleKey)
              .maybeSingle();
            setItemAllowExternal(moduleSetting?.allow_external_sharing ?? true);
          }

          // Fetch admin caller external sharing setting
          if (userRole === 'admin' && org.status === 'ready') {
            const { data: memData } = await supabase
              .from('memberships')
              .select('allow_external_sharing')
              .eq('id', org.membership.id)
              .maybeSingle();
            setAdminAllowExternal(memData?.allow_external_sharing ?? true);
          } else {
            setAdminAllowExternal(true);
          }
        }
      } catch (cause) {
        if (!signal.aborted) setError(errorCodeOf(cause));
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [adapter, subject, org, userRole],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const reload = useCallback(() => {
    const controller = new AbortController();
    void load(controller.signal);
  }, [load]);

  const toggleItemExternalSharing = async (nextValue: boolean) => {
    if ((userRole !== 'owner' && userRole !== 'developer') || itemAllowExternalSaving) return;
    setItemAllowExternalSaving(true);
    try {
      if (subject.kind === 'resource') {
        await supabase
          .from('folders_nodes')
          .update({ allow_external_sharing: nextValue })
          .eq('id', subject.resourceId);
      } else if (subject.kind === 'module' && org.status === 'ready') {
        await supabase
          .from('org_module_external_settings')
          .upsert({
            org_id: org.orgId,
            module_key: subject.moduleKey,
            allow_external_sharing: nextValue,
          });
      }
      setItemAllowExternal(nextValue);
    } catch {
      // ignore
    } finally {
      setItemAllowExternalSaving(false);
    }
  };

  // Escape closes, Tab stays inside. Both are required for a modal and neither
  // existed in the TMS original.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((element) => element.offsetParent !== null);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Restore focus to whatever opened the dialog.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    return () => opener?.focus();
  }, []);

  const executeAdd = async (targetEmail: string, targetLevel: AccessLevel) => {
    setSubmitting(true);
    setError(null);
    setNotice(null);
    setNoticeInviteLink(null);
    try {
      const outcome = await adapter.add(subject, targetEmail, targetLevel);
      if (outcome.type === 'invited') {
        const link = 'inviteLink' in outcome ? (outcome.inviteLink ?? null) : null;
        setNoticeInviteLink(link);
        setNotice(link ? 'invited' : 'invitedEmail');
      } else {
        setNotice('granted');
      }
      setEmail('');
      setLevel('view');
      reload();
    } catch (cause) {
      setError(errorCodeOf(cause));
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || submitting) return;

    const lower = trimmed.toLowerCase();
    const currentEmail = session.session?.user.email?.toLowerCase();

    const inCandidates = access?.candidates.some((c) => c.email.toLowerCase() === lower) ?? false;
    const inInherited = access?.inherited.some((c) => c.email.toLowerCase() === lower) ?? false;
    const inGrantsMember = access?.grants.some((g) => g.email.toLowerCase() === lower && g.role !== null) ?? false;
    const isSelf = currentEmail === lower;
    const isOrgMember = inCandidates || inInherited || inGrantsMember || isSelf;

    if (!isOrgMember) {
      // Non-privileged users cannot share externally at all.
      if (!isPrivileged) {
        setAlertReason('not_privileged');
        setNotInOrgAlertOpen(true);
        return;
      }
      // Admins may have external sharing disabled on their account.
      if (userRole === 'admin' && !adminAllowExternal) {
        setAlertReason('admin_disabled');
        setNotInOrgAlertOpen(true);
        return;
      }
      // The file/module may have external sharing disabled.
      if (!itemAllowExternal) {
        setAlertReason('item_disabled');
        setNotInOrgAlertOpen(true);
        return;
      }
      // External sharing is enabled: go straight to the worker.
      // The worker decides whether the email has a Supabase account (direct
      // grant) or needs an invitation token (kept in DB, sharer copies link).
      // No intermediate prompt needed. Store pendingEmail in case the owner
      // later opens the "add to organization" sub-dialog from elsewhere.
      setPendingEmail(trimmed);
      await executeAdd(trimmed, level);
      return;
    }

    await executeAdd(trimmed, level);
  };

  const handleShareWithoutAdding = async () => {
    setExternalPromptOpen(false);
    await executeAdd(pendingEmail, level);
  };

  const handleOpenAddToOrg = async () => {
    setExternalPromptOpen(false);
    setAddToOrgError(null);
    setAddName('');
    setAddRole('member');
    setAddTeam('');

    if (org.status === 'ready') {
      const { data } = await supabase
        .from('teams')
        .select('id, name')
        .eq('org_id', org.orgId)
        .order('name');
      setTeamsList(data ?? []);
    }
    setAddToOrgOpen(true);
  };

  const handleConfirmAddAndShare = async (event: FormEvent) => {
    event.preventDefault();
    if (addingToOrgBusy) return;
    setAddingToOrgBusy(true);
    setAddToOrgError(null);

    try {
      const token = session.session?.access_token;
      const response = await fetch('/api/invitations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(org.status === 'ready' ? { 'X-Active-Org': org.orgId } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          email: pendingEmail,
          kind: 'membership',
          role: addRole,
          teamId: addTeam || null,
        }),
      });

      if (!response.ok) {
        setAddToOrgError(t('users.error.invite'));
        setAddingToOrgBusy(false);
        return;
      }

      setAddToOrgOpen(false);
      setAddingToOrgBusy(false);
      await executeAdd(pendingEmail, level);
    } catch {
      setAddToOrgError(t('users.error.invite'));
      setAddingToOrgBusy(false);
    }
  };

  const changeLevel = async (grant: ShareGrant, next: AccessLevel) => {
    if (!access) return;
    const previous = access;
    setError(null);
    setAccess({
      ...access,
      grants: access.grants.map((row) =>
        row.userId === grant.userId ? { ...row, level: next } : row,
      ),
    });
    try {
      await adapter.changeLevel(subject, grant.userId, next);
    } catch (cause) {
      setError(errorCodeOf(cause));
      setAccess(previous);
    }
  };

  const revoke = async (grant: ShareGrant) => {
    if (!access) return;
    const previous = access;
    setError(null);
    setAccess({
      ...access,
      grants: access.grants.filter((row) => row.userId !== grant.userId),
    });
    try {
      await adapter.revoke(subject, grant.userId);
      reload();
    } catch (cause) {
      setError(errorCodeOf(cause));
      setAccess(previous);
    }
  };

  const cancelInvite = async (invitationId: string) => {
    if (!access) return;
    const previous = access;
    setError(null);
    setAccess({
      ...access,
      pendingInvites: access.pendingInvites.filter(
        (row) => row.invitationId !== invitationId,
      ),
    });
    try {
      await adapter.cancelInvite(subject, invitationId);
    } catch (cause) {
      setError(errorCodeOf(cause));
      setAccess(previous);
    }
  };

  const answerRequest = async (request: AccessRequest, approve: boolean) => {
    if (!access) return;
    const previous = access;
    setError(null);
    setAccess({
      ...access,
      accessRequests: (access.accessRequests ?? []).filter((row) => row.requestId !== request.requestId),
    });
    try {
      if (approve) {
        await adapter.approveRequest?.(subject, request);
        reload();
      } else {
        await adapter.dismissRequest?.(subject, request.requestId);
      }
    } catch (cause) {
      setError(errorCodeOf(cause));
      setAccess(previous);
    }
  };

  const canAdd = access?.canAdd ?? false;

  const changePublicAccess = async (event: ChangeEvent<HTMLSelectElement>) => {
    const next = event.target.value === 'public';
    if (!publicAccess || !adapter.setPublic || !publicAccess.canManage || publicSaving) return;
    setPublicSaving(true);
    setError(null);
    try {
      await adapter.setPublic(subject, next);
      setPublicAccess({ ...publicAccess, isPublic: next });
      if (subject.kind === 'resource') {
        window.dispatchEvent(new CustomEvent('sheet:public-changed', { detail: { id: subject.resourceId, isPublic: next } }));
      }
    } catch (cause) {
      setError(errorCodeOf(cause));
    } finally {
      setPublicSaving(false);
    }
  };

  const copyPublicLink = async () => {
    if (!publicAccess) return;
    await navigator.clipboard.writeText(shareLink(publicAccess.url, window.location.origin));
    setLinkCopied(true);
    window.setTimeout(() => setLinkCopied(false), 1500);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[var(--overlay)] p-0 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
        className={[
          // Full-width sheet on a phone, centered card from `sm` up.
          'flex max-h-[92dvh] w-full max-w-lg flex-col overflow-y-auto',
          'rounded-t-token-lg bg-surface p-4 text-fg sm:rounded-token-lg sm:p-6',
          'pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-6',
          'border border-border shadow-[0_4px_16px_rgb(15_23_42_/_0.08)]',
        ].join(' ')}
      >
        <header className="flex items-start justify-between gap-3">
          <h2 id={titleId} className="text-base font-semibold sm:text-lg">
            {t('share.title', { name: subject.name })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="tap-target -mr-2 -mt-2 flex items-center justify-center rounded-full text-muted hover:text-fg"
          >
            <Icon name="close" />
          </button>
        </header>

        <p className="mt-1 text-sm text-muted">
          {t(
            subject.kind === 'module'
              ? 'share.subtitleModule'
              : 'share.subtitleResource',
          )}
        </p>

        {(userRole === 'owner' || userRole === 'developer') && (
          <div className="mt-3 flex items-center justify-between rounded-token border border-border bg-surface-2 p-3 text-sm">
            <span className="font-medium text-fg">{t('share.allowExternalSharingLabel')}</span>
            <input
              type="checkbox"
              checked={itemAllowExternal}
              disabled={itemAllowExternalSaving}
              onChange={(e) => void toggleItemExternalSharing(e.target.checked)}
              className="h-4 w-4 rounded border-border-strong text-accent focus:ring-accent"
            />
          </div>
        )}

        {error && (
          <p
            role="alert"
            className="mt-3 rounded-token border border-danger px-3 py-2 text-sm text-danger"
          >
            {t(`share.error.${error}`)}
          </p>
        )}

        {notice && (
          <div
            role="status"
            className="mt-3 rounded-token border border-success px-3 py-2 text-sm text-success"
          >
            <p>
              {t(
                notice === 'granted'
                  ? 'share.notice.granted'
                  : notice === 'invitedEmail'
                  ? 'share.notice.invitedEmail'
                  : 'share.notice.invited',
              )}
            </p>
            {notice === 'invited' && noticeInviteLink && (
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(noticeInviteLink).then(() => {
                    setInviteLinkCopied(true);
                    window.setTimeout(() => setInviteLinkCopied(false), 1500);
                  });
                }}
                className="tap-target mt-2 rounded-full border border-success px-3 text-xs font-medium text-success hover:bg-success/10"
              >
                🔗 {inviteLinkCopied ? t('share.notice.inviteLinkCopied') : t('share.notice.copyInviteLink')}
              </button>
            )}
          </div>
        )}

        {canAdd && (
          <form onSubmit={submit} className="mt-4 flex flex-wrap items-end gap-2">
            <div className="min-w-0 flex-1 basis-48">
              <label htmlFor={emailFieldId} className="mb-1 block text-sm text-muted">
                {t('share.addLabel')}
              </label>
              <input
                id={emailFieldId}
                type="email"
                list={listId}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t('share.addPlaceholder')}
                autoComplete="off"
                className="tap-target w-full rounded-full border border-border-strong bg-surface px-3 text-sm text-fg placeholder:text-muted"
              />
              {/* Members of THIS organization only. A directory that crosses
                  tenants would leak one customer's staff to another. */}
              <datalist id={listId}>
                {access?.candidates.map((candidate) => (
                  <option key={candidate.userId} value={candidate.email}>
                    {candidate.name ?? candidate.email}
                  </option>
                ))}
              </datalist>
            </div>
            <AccessLevelSelect
              value={level}
              onChange={setLevel}
              label={t('share.addLabel')}
              className="w-32 shrink-0"
            />
            <button
              type="submit"
              disabled={!email.trim() || submitting}
              className="tap-target shrink-0 rounded-full bg-accent px-4 text-sm font-medium text-accent-fg hover:bg-accent-hover disabled:opacity-50"
            >
              {submitting ? t('share.sharing') : t('share.share')}
            </button>
          </form>
        )}

        {loading ? (
          <p className="mt-5 text-sm text-muted">{t('common.loading')}</p>
        ) : (
          <>
            <Section title={t('share.withAccess')}>
              {access && access.grants.length > 0 ? (
                <PersonList>
                  {access.grants.map((grant) => (
                    <PersonRow
                      key={grant.userId}
                      name={grant.name}
                      email={grant.email}
                      caption={
                        grant.role
                          ? t(`share.role.${grant.role}`)
                          : t('share.outsideCollaborator')
                      }
                    >
                      <AccessLevelSelect
                        value={grant.level}
                        onChange={(next) => void changeLevel(grant, next)}
                        readOnly={!grant.canManage}
                        label={grant.name ?? grant.email}
                        className="w-32 shrink-0"
                      />
                      {grant.canManage && (
                        <button
                          type="button"
                          onClick={() => void revoke(grant)}
                          aria-label={t('share.revokeFor', {
                            name: grant.name ?? grant.email,
                          })}
                          className="tap-target flex shrink-0 items-center justify-center rounded-full text-muted hover:text-danger"
                        >
                          <Icon name="close" className="h-4 w-4" />
                        </button>
                      )}
                    </PersonRow>
                  ))}
                </PersonList>
              ) : (
                <p className="text-sm text-muted">{t('share.nobodyYet')}</p>
              )}
            </Section>

            {access?.accessRequests && access.accessRequests.length > 0 && adapter.approveRequest && (
              <Section title={t('share.requests')}>
                <PersonList>
                  {access.accessRequests.map((request) => (
                    <PersonRow
                      key={request.requestId}
                      name={null}
                      email={request.email}
                      caption={t('share.requestCaption')}
                      icon="mail"
                    >
                      <button
                        type="button"
                        onClick={() => void answerRequest(request, true)}
                        className="tap-target shrink-0 rounded-token bg-accent px-3 text-sm font-medium text-accent-fg"
                      >
                        {t('share.approveRequest')}
                      </button>
                      <button
                        type="button"
                        onClick={() => void answerRequest(request, false)}
                        aria-label={t('share.dismissRequestFor', { name: request.email })}
                        className="tap-target flex shrink-0 items-center justify-center rounded-full text-muted hover:text-danger"
                      >
                        <Icon name="close" className="h-4 w-4" />
                      </button>
                    </PersonRow>
                  ))}
                </PersonList>
              </Section>
            )}

            {access && access.pendingInvites.length > 0 && (
              <Section title={t('share.pending')}>
                <PersonList>
                  {access.pendingInvites.map((invite) => (
                    <PersonRow
                      key={invite.invitationId}
                      name={null}
                      email={invite.email}
                      caption={t('share.pendingCaption')}
                      icon="mail"
                    >
                      <AccessLevelSelect
                        value={invite.level}
                        onChange={() => {}}
                        readOnly
                        label={invite.email}
                        className="w-32 shrink-0"
                      />
                      {canAdd && (
                        <button
                          type="button"
                          onClick={() => void cancelInvite(invite.invitationId)}
                          aria-label={t('share.cancelInviteFor', { name: invite.email })}
                          className="tap-target flex shrink-0 items-center justify-center rounded-full text-muted hover:text-danger"
                        >
                          <Icon name="close" className="h-4 w-4" />
                        </button>
                      )}
                    </PersonRow>
                  ))}
                </PersonList>
              </Section>
            )}

            {access && access.inherited.length > 0 && (
              <Section title={t('share.fullAccessByRole')}>
                <PersonList>
                  {access.inherited.map((person) => (
                    <PersonRow
                      key={person.userId}
                      name={person.name}
                      email={person.email}
                      caption={person.role ? t(`share.role.${person.role}`) : ''}
                    >
                      <AccessLevelSelect
                        value={person.level ?? 'edit_formula'}
                        onChange={() => {}}
                        readOnly={!person.canManage}
                        label={person.name ?? person.email}
                        className="w-32 shrink-0"
                      />
                    </PersonRow>
                  ))}
                </PersonList>
              </Section>
            )}

            {publicAccess && (
              <Section title={t('share.generalAccess')}>
                <div className="rounded-token border border-border bg-surface-2 p-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-3 text-lg">{publicAccess.isPublic ? '🌐' : '🔒'}</span>
                    <div className="min-w-0 flex-1">
                      <label htmlFor={`${titleId}-general-access`} className="block text-sm font-medium text-fg">{t('share.generalAccess')}</label>
                      <select
                        id={`${titleId}-general-access`}
                        value={publicAccess.isPublic ? 'public' : 'restricted'}
                        onChange={(event) => void changePublicAccess(event)}
                        disabled={!publicAccess.canManage || publicSaving || !adapter.setPublic}
                        aria-label={t('share.generalAccess')}
                        className="tap-target mt-1 max-w-full rounded-full border border-border-strong bg-surface px-2 text-sm text-fg disabled:opacity-60"
                      >
                        <option value="restricted">{t('share.restricted')}</option>
                        <option value="public">{t('share.anyoneWithLink')}</option>
                      </select>
                      <p className="mt-1 text-xs text-muted">{t(publicAccess.isPublic ? 'share.anyoneWithLinkDescription' : 'share.restrictedDescription')}</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => void copyPublicLink()} className="tap-target mt-3 rounded-full border border-border-strong px-3 text-sm text-accent hover:bg-accent-soft" aria-label={t('share.copyLink')} title={t('share.copyLink')}>
                    <span aria-hidden="true">🔗</span> {linkCopied ? t('share.linkCopied') : t('share.copyLink')}
                  </button>
                </div>
              </Section>
            )}
          </>
        )}

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="tap-target rounded-full border border-border-strong px-4 text-sm text-fg hover:border-accent"
          >
            {t('common.close')}
          </button>
        </div>
      </div>

      {externalPromptOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-[var(--overlay)] p-4"
          onClick={() => setExternalPromptOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-token-lg border border-border bg-surface p-5 shadow-card text-fg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold">
              {t('share.notInOrgTitle')}
            </h3>
            <p className="mt-2 text-sm text-muted">
              {t('share.notInOrgMessage', { email: pendingEmail })}
            </p>
            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => void handleOpenAddToOrg()}
                className="tap-target w-full rounded-full bg-accent px-4 py-2 text-sm font-medium text-accent-fg hover:bg-accent-hover"
              >
                {t('share.shareAndAdd')}
              </button>
              <button
                type="button"
                onClick={() => void handleShareWithoutAdding()}
                className="tap-target w-full rounded-full border border-border-strong px-4 py-2 text-sm font-medium text-fg hover:border-accent"
              >
                {t('share.shareWithoutAdd')}
              </button>
              <button
                type="button"
                onClick={() => setExternalPromptOpen(false)}
                className="tap-target w-full rounded-full border border-border px-4 py-2 text-sm text-muted hover:text-fg"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {notInOrgAlertOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-[var(--overlay)] p-4"
          onClick={() => setNotInOrgAlertOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-token-lg border border-border bg-surface p-5 shadow-card text-fg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold">
              {t('share.notInOrgTitle')}
            </h3>
            <p className="mt-2 text-sm text-danger">
              {t(
                alertReason === 'admin_disabled'
                  ? 'share.adminExternalSharingDisabled'
                  : alertReason === 'item_disabled'
                  ? 'share.itemExternalSharingDisabled'
                  : 'share.notInOrgAlert',
              )}
            </p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setNotInOrgAlertOpen(false)}
                className="tap-target rounded-full bg-accent px-4 text-sm font-medium text-accent-fg hover:bg-accent-hover"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {addToOrgOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-[var(--overlay)] p-4"
          onClick={() => setAddToOrgOpen(false)}
        >
          <form
            onSubmit={(e) => void handleConfirmAddAndShare(e)}
            className="w-full max-w-md rounded-token-lg border border-border bg-surface p-5 shadow-card text-fg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold">
                {t('share.addToOrgTitle')}
              </h3>
              <button
                type="button"
                onClick={() => setAddToOrgOpen(false)}
                className="tap-target rounded-full text-muted hover:text-fg"
                aria-label={t('common.close')}
              >
                <Icon name="close" />
              </button>
            </div>

            {addToOrgError && (
              <p className="mt-2 rounded-token border border-danger px-3 py-1.5 text-xs text-danger">
                {addToOrgError}
              </p>
            )}

            <div className="mt-4 flex flex-col gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted">
                  {t('users.email')}
                </label>
                <input
                  type="email"
                  value={pendingEmail}
                  disabled
                  className="tap-target w-full rounded-full border border-border bg-surface-2 px-3 text-sm text-muted opacity-75"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-muted">
                  {t('share.nameLabel')}
                </label>
                <input
                  type="text"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  placeholder={t('share.nameLabel')}
                  className="tap-target w-full rounded-full border border-border-strong bg-surface px-3 text-sm text-fg placeholder:text-muted"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-muted">
                  {t('share.roleLabel')}
                </label>
                <select
                  value={addRole}
                  onChange={(e) => setAddRole(e.target.value as OrgRole)}
                  className="tap-target w-full rounded-full border border-border-strong bg-surface px-3 text-sm text-fg"
                >
                  {LADDER.map((r) => (
                    <option key={r} value={r}>
                      {t(`share.role.${r}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-muted">
                  {t('share.teamLabel')}
                </label>
                <select
                  value={addTeam}
                  onChange={(e) => setAddTeam(e.target.value)}
                  className="tap-target w-full rounded-full border border-border-strong bg-surface px-3 text-sm text-fg"
                >
                  <option value="">{t('share.noTeam')}</option>
                  {teamsList.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setAddToOrgOpen(false)}
                className="tap-target rounded-full border border-border px-4 text-sm font-medium text-fg hover:border-accent"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={addingToOrgBusy}
                className="tap-target rounded-full bg-accent px-4 text-sm font-medium text-accent-fg hover:bg-accent-hover disabled:opacity-50"
              >
                {addingToOrgBusy ? t('common.loading') : t('share.confirmAddAndShare')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>,
    document.body,
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-5">
      <h3 className="mb-2 text-sm font-semibold text-muted">{title}</h3>
      {children}
    </section>
  );
}

function PersonList({ children }: { children: React.ReactNode }) {
  return (
    <ul className="divide-y divide-border rounded-token border border-border">
      {children}
    </ul>
  );
}

function PersonRow({
  name,
  email,
  caption,
  icon,
  children,
}: Pick<SharePrincipal, 'name' | 'email'> & {
  caption: string;
  icon?: 'mail';
  children: React.ReactNode;
}) {
  return (
    <li className="flex flex-wrap items-center gap-3 p-3">
      <span
        aria-hidden="true"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-semibold text-accent"
      >
        {icon ? <Icon name={icon} className="h-4 w-4" /> : initials(name, email)}
      </span>
      <span className="min-w-0 flex-1 basis-40">
        <span className="block truncate text-sm font-medium text-fg">
          {name ?? email}
        </span>
        <span className="block truncate text-xs text-muted">
          {name ? `${email}${caption ? ` · ${caption}` : ''}` : caption}
        </span>
      </span>
      {children}
    </li>
  );
}
